import type { AxiosResponse } from 'axios'

import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import type { ApiResponse } from '@/api/types'
import type { BonusItemLine } from '@/modules/inventory/bonus-items/types'
import type { AdjustmentBatchRow } from '@/services/inventoryAdjustmentService'

/** POST /api/Bonus/Save & PUT /api/Bonus/Update body (Swagger). */
export type BonusSchemeLinePayload = {
  id: number
  bonusId: number
  promotionItemId: number
  promotionBatchId: number
  purchaseQuantity: number
  bonusItemId: number
  bonusBatchId: number
  bonusQuantity: number
  isDeleted: string
}

export type BonusSchemeSavePayload = {
  id: number
  code: string
  date: string
  schemeStartDate: string
  schemeEndDate: string
  branchId: number
  itemList: BonusSchemeLinePayload[]
}

/** Map HTML date (yyyy-mm-dd) to API ISO string. */
export function bonusDateInputToApiIso(dateStr: string): string {
  const s = (dateStr || '').trim()
  if (!s) return new Date().toISOString()
  if (s.includes('T')) {
    const d = new Date(s)
    return Number.isNaN(d.getTime()) ? new Date().toISOString() : d.toISOString()
  }
  return `${s}T12:00:00.000Z`
}

function unwrap(payload: unknown): unknown {
  if (payload && typeof payload === 'object') {
    const p = payload as Record<string, unknown>
    if ('success' in p && 'data' in p) return p.data
    if ('data' in p) return p.data
  }
  return payload
}

function toArray(payload: unknown): any[] {
  const raw = unwrap(payload)
  if (Array.isArray(raw)) return raw
  if (raw && typeof raw === 'object' && Array.isArray((raw as { data?: unknown }).data)) {
    return (raw as { data: any[] }).data
  }
  if (raw && typeof raw === 'object') return [raw]
  return []
}

export type BonusItemOption = { value: string; label: string }

function mapAvailableItem(x: Record<string, unknown>): BonusItemOption | null {
  const id = Number(x?.itemId ?? x?.ItemId ?? x?.id ?? x?.Id ?? 0)
  if (!Number.isFinite(id) || id <= 0) return null
  const code = String(x?.itemCode ?? x?.ItemCode ?? x?.code ?? x?.Code ?? '').trim()
  const nameEn = String(x?.itemNameEn ?? x?.ItemNameEn ?? x?.nameEn ?? x?.NameEn ?? '').trim()
  const nameAr = String(x?.itemNameAr ?? x?.ItemNameAr ?? x?.nameAr ?? x?.NameAr ?? '').trim()
  const name = String(x?.name ?? x?.Name ?? '').trim()
  const label =
    [code, nameEn || nameAr || name].filter(Boolean).join(' — ').trim() || String(id)
  return { value: String(id), label }
}

function mapAvailableBatch(x: Record<string, unknown>): AdjustmentBatchRow | null {
  const id = Number(x?.batchId ?? x?.BatchId ?? x?.id ?? x?.Id ?? 0)
  if (!Number.isFinite(id) || id <= 0) return null
  const code = String(x?.code ?? x?.Code ?? x?.batchCode ?? x?.BatchCode ?? '').trim()
  const reference = String(x?.reference ?? x?.Reference ?? '').trim()
  const availableQuantity = Number(
    x?.availableQuantity ?? x?.AvailableQuantity ?? x?.quantity ?? x?.Quantity ?? 0
  )
  return {
    id,
    code: code || reference || String(id),
    reference,
    availableQuantity: Number.isFinite(availableQuantity) ? availableQuantity : 0,
  }
}

/**
 * Items with positive available quantity anywhere under the branch (per Bonus API).
 */
export async function getBonusAvailableItems(branchId: number): Promise<BonusItemOption[]> {
  const url = endpoints.inventoryBonus.getAvailableItems(branchId)
  const response = await apiClient.get<ApiResponse<any> | any>(url)
  const arr = toArray(response.data)
  const out: BonusItemOption[] = []
  for (const row of arr) {
    const opt = mapAvailableItem(row && typeof row === 'object' ? (row as Record<string, unknown>) : {})
    if (opt) out.push(opt)
  }
  return out
}

/**
 * Batches with stock under the branch; pass itemId to narrow to that item (query param).
 */
export async function getBonusAvailableBatches(
  branchId: number,
  itemId: number
): Promise<AdjustmentBatchRow[]> {
  const url = endpoints.inventoryBonus.getAvailableBatches(branchId, itemId)
  const response = await apiClient.get<ApiResponse<any> | any>(url)
  const arr = toArray(response.data)
  const out: AdjustmentBatchRow[] = []
  for (const row of arr) {
    const b = mapAvailableBatch(row && typeof row === 'object' ? (row as Record<string, unknown>) : {})
    if (b) out.push(b)
  }
  return out
}

export async function saveBonusScheme(
  payload: BonusSchemeSavePayload
): Promise<AxiosResponse<unknown>> {
  return apiClient.post<unknown>(endpoints.inventoryBonus.save, payload)
}

export async function updateBonusScheme(
  payload: BonusSchemeSavePayload
): Promise<AxiosResponse<unknown>> {
  return apiClient.put<unknown>(endpoints.inventoryBonus.update, payload)
}

/** Unwrap typical ApiResponse { data: ... } or return root. */
export function unwrapBonusResponseBody(raw: unknown): Record<string, unknown> | null {
  if (raw == null || typeof raw !== 'object') return null
  const o = raw as Record<string, unknown>
  const inner = o.data ?? o.Data
  if (inner != null && typeof inner === 'object' && !Array.isArray(inner)) {
    return inner as Record<string, unknown>
  }
  return o
}

export function extractBonusMasterIdFromResponse(raw: unknown): number {
  const p = unwrapBonusResponseBody(raw)
  if (!p) return 0
  const id = Number(p.id ?? p.Id ?? p.bonusId ?? p.BonusId ?? 0)
  return Number.isFinite(id) && id > 0 ? id : 0
}

export function extractBonusLineIdsFromResponse(raw: unknown): number[] {
  const p = unwrapBonusResponseBody(raw)
  if (!p) return []
  const list = (p.itemList ?? p.ItemList) as unknown
  if (!Array.isArray(list)) return []
  return list.map((row) => {
    if (!row || typeof row !== 'object') return 0
    const r = row as Record<string, unknown>
    const id = Number(r.id ?? r.Id ?? 0)
    return Number.isFinite(id) && id > 0 ? id : 0
  })
}

// --- List / detail (GetAll, GetById, GetLinesByMasterId) ---

export type BonusSchemeListRow = {
  id: string
  serverBonusId: number
  code: string
  date: string
  startDate: string
  endDate: string
  warehouseLabel: string
}

export type BonusSchemeMasterVm = {
  id: number
  code: string
  date: string
  schemeStartDate: string
  schemeEndDate: string
  branchId: number
}

function dateToInputValue(raw: unknown): string {
  const s = String(raw ?? '').trim()
  if (!s) return ''
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s
  if (s.length >= 10 && /^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10)
  const d = new Date(s)
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10)
}

function mapBonusListRow(x: Record<string, unknown>): BonusSchemeListRow {
  const serverBonusId = Number(x.id ?? x.Id ?? 0)
  return {
    id: serverBonusId > 0 ? String(serverBonusId) : `tmp-${Math.random().toString(36).slice(2, 9)}`,
    serverBonusId,
    code: String(x.code ?? x.Code ?? ''),
    date: dateToInputValue(x.date ?? x.Date),
    startDate: dateToInputValue(
      x.schemeStartDate ?? x.SchemeStartDate ?? x.startDate ?? x.StartDate,
    ),
    endDate: dateToInputValue(x.schemeEndDate ?? x.SchemeEndDate ?? x.endDate ?? x.EndDate),
    warehouseLabel: String(
      x.warehouseName ?? x.WarehouseName ?? x.warehouse ?? x.Warehouse ?? '',
    ).trim(),
  }
}

function pickBonusMasterRow(payload: unknown): Record<string, unknown> {
  const raw = unwrap(payload)
  if (Array.isArray(raw) && raw[0] && typeof raw[0] === 'object') {
    return raw[0] as Record<string, unknown>
  }
  if (raw && typeof raw === 'object') {
    const p = raw as Record<string, unknown>
    const inner = p.data ?? p.Data
    if (Array.isArray(inner) && inner[0] && typeof inner[0] === 'object') {
      return inner[0] as Record<string, unknown>
    }
    if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
      return inner as Record<string, unknown>
    }
    return p
  }
  return {}
}

export function mapBonusMasterVm(row: Record<string, unknown>): BonusSchemeMasterVm {
  return {
    id: Number(row.id ?? row.Id ?? 0),
    code: String(row.code ?? row.Code ?? ''),
    date: dateToInputValue(row.date ?? row.Date),
    schemeStartDate: dateToInputValue(
      row.schemeStartDate ?? row.SchemeStartDate ?? row.startDate ?? row.StartDate,
    ),
    schemeEndDate: dateToInputValue(
      row.schemeEndDate ?? row.SchemeEndDate ?? row.endDate ?? row.EndDate,
    ),
    branchId: Number(row.branchId ?? row.BranchId ?? 0),
  }
}

function newLineReactId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `ln-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

export function mapApiBonusLineToFormLine(x: Record<string, unknown>, reactKey: string): BonusItemLine {
  const sid = Number(x.id ?? x.Id ?? 0)
  const pi = Number(x.promotionItemId ?? x.PromotionItemId ?? 0)
  const pb = Number(x.promotionBatchId ?? x.PromotionBatchId ?? 0)
  const bi = Number(x.bonusItemId ?? x.BonusItemId ?? 0)
  const bb = Number(x.bonusBatchId ?? x.BonusBatchId ?? 0)
  const pq = x.purchaseQuantity ?? x.PurchaseQuantity ?? x.promotionQuantity ?? x.PromotionQuantity
  const bq = x.bonusQuantity ?? x.BonusQuantity
  return {
    id: reactKey,
    serverLineId: sid > 0 ? sid : undefined,
    promotionItemId: pi > 0 ? String(pi) : '',
    promotionBatchId: pb > 0 ? String(pb) : '',
    promotionQty: pq != null && String(pq).trim() !== '' ? String(pq) : '',
    bonusItemId: bi > 0 ? String(bi) : '',
    bonusBatchId: bb > 0 ? String(bb) : '',
    bonusQty: bq != null && String(bq).trim() !== '' ? String(bq) : '',
  }
}

export async function getAllBonusSchemes(
  branchId: number,
  pageNumber: number,
  pageSize: number,
): Promise<{ items: BonusSchemeListRow[]; totalCount: number }> {
  const url = endpoints.inventoryBonus.getAll(branchId, pageNumber, pageSize)
  const response = await apiClient.get<ApiResponse<unknown> | unknown>(url)
  const payload = unwrap(response.data)

  let itemsRaw: unknown[] = []
  let totalCount = 0

  if (Array.isArray(payload)) {
    itemsRaw = payload
    totalCount = payload.length
  } else if (payload && typeof payload === 'object') {
    const p = payload as Record<string, unknown>
    const data = p.data ?? p.Data ?? p.items ?? p.Items
    if (Array.isArray(data)) itemsRaw = data
    totalCount = Number(
      p.totalCount ??
        p.TotalCount ??
        p.totalRecords ??
        p.TotalRecords ??
        p.total ??
        p.Total ??
        itemsRaw.length,
    )
    if (!Number.isFinite(totalCount)) totalCount = itemsRaw.length
  }

  const items = itemsRaw.map((row) =>
    mapBonusListRow(row && typeof row === 'object' ? (row as Record<string, unknown>) : {}),
  )
  return { items, totalCount }
}

export async function getBonusMasterById(branchId: number, id: number): Promise<BonusSchemeMasterVm> {
  const url = endpoints.inventoryBonus.getById(branchId, id)
  const response = await apiClient.get<ApiResponse<unknown> | unknown>(url)
  const row = pickBonusMasterRow(response.data)
  return mapBonusMasterVm(row)
}

export async function deleteBonusScheme(branchId: number, id: number): Promise<void> {
  await apiClient.delete(endpoints.inventoryBonus.delete(branchId, id))
}

export async function getBonusLinesByMasterId(
  branchId: number,
  masterId: number,
): Promise<BonusItemLine[]> {
  const url = endpoints.inventoryBonus.getLinesByMasterId(branchId, masterId)
  const response = await apiClient.get<ApiResponse<any> | any>(url)
  const arr = toArray(response.data)
  return arr.map((row) =>
    mapApiBonusLineToFormLine(
      row && typeof row === 'object' ? (row as Record<string, unknown>) : {},
      newLineReactId(),
    ),
  )
}

export const bonusSchemeService = {
  getAvailableItems: getBonusAvailableItems,
  getAvailableBatches: getBonusAvailableBatches,
  save: saveBonusScheme,
  update: updateBonusScheme,
  delete: deleteBonusScheme,
  getAll: getAllBonusSchemes,
  getById: getBonusMasterById,
  getLinesByMasterId: getBonusLinesByMasterId,
}
